// Macros dos campos numéricos da importação de XML (Preferências > XML).
//
// Os campos Valor, Valor da Unidade, Quantidade (Item) e Quantidade de Tags
// aceitam, além do nome de uma tag do XML, uma expressão combinando:
//   - tags do XML            → qCom, vUnCom, qTrib...
//   - números                → 2, 0.5, 0,5
//   - infos da categoria     → info.compl.1, info compl 2, inf_compl_3, info4
// com os operadores + - * / (multiplicação/divisão calculadas antes).
//
// Ex.: "qCom/info.compl.1" → valor da tag qCom dividido pela Info. Compl. 1
// da categoria cadastrada na base (casada pelo código do item).
//
// Se algum operando não resolver (tag ausente, categoria sem a info, valor
// não numérico ou divisão por zero), a macro cai para o valor do primeiro
// operando — "qCom/info.compl.1" sem a info vira só "qCom".

export type OperadorMacro = "+" | "-" | "*" | "/";

export type OperandoMacro =
  | { tipo: "numero"; valor: number }
  | { tipo: "info"; indice: number }
  | { tipo: "tag"; nome: string };

export type MacroXml = {
  operandos: OperandoMacro[];
  operadores: OperadorMacro[];
};

// "info. compl.1", "info compl 1", "inf_compl_1", "info1"... (case-insensitive)
const RE_INFO = /^inf(?:o)?[.\s_]*(?:compl)?[.\s_]*(\d{1,2})$/i;
const RE_NUMERO = /^\d+(?:[.,]\d+)?$/;
const RE_TAG = /^[A-Za-z_][A-Za-z0-9_:]*$/;

/**
 * Interpreta o valor configurado no campo como macro. Retorna `null` quando o
 * valor não é uma macro (sem operadores) ou é inválido — nesses casos o campo
 * segue o comportamento antigo (nome de tag simples).
 */
export function analisarMacroXml(campo?: string | null): MacroXml | null {
  const bruto = campo?.trim();
  if (!bruto) return null;
  if (!/[+\-*/]/.test(bruto)) return null;

  const tokens = bruto.split(/([+\-*/])/).map((tk) => tk.trim());
  // Precisa alternar operando/operador e terminar em operando.
  if (tokens.length < 3 || tokens.length % 2 === 0) return null;

  const operandos: OperandoMacro[] = [];
  const operadores: OperadorMacro[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const tk = tokens[i];
    if (!tk) return null;
    if (i % 2 === 1) {
      operadores.push(tk as OperadorMacro);
      continue;
    }
    const infoMatch = tk.match(RE_INFO);
    if (infoMatch) {
      const indice = Number(infoMatch[1]);
      if (indice < 1 || indice > 12) return null;
      operandos.push({ tipo: "info", indice });
    } else if (RE_NUMERO.test(tk)) {
      operandos.push({ tipo: "numero", valor: Number(tk.replace(",", ".")) });
    } else if (RE_TAG.test(tk)) {
      operandos.push({ tipo: "tag", nome: tk });
    } else {
      return null;
    }
  }

  return { operandos, operadores };
}

/** Indica se a macro depende de alguma info complementar da categoria. */
export function macroUsaInfoCategoria(macro: MacroXml | null): boolean {
  return !!macro?.operandos.some((op) => op.tipo === "info");
}

/** Converte texto em número (aceita vírgula decimal). `null` quando inválido. */
export function numeroDeTexto(texto?: string | null): number | null {
  const s = texto?.trim();
  if (!s) return null;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/**
 * Avalia a macro com precedência (`*` e `/` antes de `+` e `-`). Retorna
 * `null` se qualquer operando não resolver ou houver divisão por zero.
 */
export function avaliarMacroXml(
  macro: MacroXml,
  resolver: (op: OperandoMacro) => number | null,
): number | null {
  const valores = macro.operandos.map(resolver);
  if (valores.some((v) => v === null)) return null;
  const nums = valores as number[];

  // 1ª passada: reduz * e / em cima do operando à esquerda.
  const parcelas: number[] = [nums[0]];
  const somas: ("+" | "-")[] = [];
  for (let i = 0; i < macro.operadores.length; i++) {
    const op = macro.operadores[i];
    const v = nums[i + 1];
    if (op === "*" || op === "/") {
      if (op === "/" && v === 0) return null;
      parcelas[parcelas.length - 1] =
        op === "*"
          ? parcelas[parcelas.length - 1] * v
          : parcelas[parcelas.length - 1] / v;
    } else {
      somas.push(op);
      parcelas.push(v);
    }
  }

  // 2ª passada: + e - na ordem.
  let total = parcelas[0];
  for (let i = 0; i < somas.length; i++) {
    total = somas[i] === "+" ? total + parcelas[i + 1] : total - parcelas[i + 1];
  }
  return Number.isFinite(total) ? total : null;
}

/**
 * Avalia a macro; quando ela não resolve por inteiro, cai para o valor do
 * primeiro operando (regra de degradação documentada no topo do arquivo).
 */
export function avaliarMacroComFallback(
  macro: MacroXml,
  resolver: (op: OperandoMacro) => number | null,
): number | null {
  const total = avaliarMacroXml(macro, resolver);
  if (total !== null) return total;
  return resolver(macro.operandos[0]);
}

/**
 * Formata o resultado truncado em 2 casas decimais (0.909091 → "0.90").
 * Inteiros saem sem casas ("11"); o epsilon corrige ruído de float
 * (0.8999999 → "0.90").
 */
export function formatarResultadoMacro(valor: number): string {
  const truncado = Math.trunc((valor + 1e-9) * 100) / 100;
  return Number.isInteger(truncado) ? String(truncado) : truncado.toFixed(2);
}
